
import React, {useState, useEffect} from 'react'
import {Table, Form, Button, Spinner, Badge, Dropdown, ButtonGroup, InputGroup, Modal} from 'react-bootstrap'
import {useLazyQuery, useMutation} from '@apollo/client'
import {GET_ALL_RECIPIENTS} from '../../../gql/queries/contactsApiQueries'
import {REMOVE_FROM_RECIPIENTS} from '../../../gql/mutations/recipients'
import {BULK_UNSUBSCRIBE_CONTACTS} from '../../../gql/mutations/subscription'
import {IResRecipientsAPI} from '../../../types/contactsApi'
import {RecipientFilters as FilterTypes, RecipientStats} from '../recipientsTypes'
import {PaginationSection} from '../../shared/PaginationSection'
import {PaginationUtils} from '../../shared/PaginationUtils'
import {BulkTagModal} from './BulkTagModal'
import {ModalPreview} from '../../contacts/components/ModalPreview'
import {PhoneProviderActions} from '../../../components/telephony/PhoneProviderActions'
import {getTagContrastColor} from '../../../helpers'
import {
  IMMIGRATION_STATUS_CHOICES,
  DOCUMENT_STATUS_OPTIONS,
  PAYMENT_STATUS_OPTIONS,
  GENDER_OPTIONS,
  METAL_LEVELS,
} from '../../../helpers/catalogs'
import {toast} from 'react-toastify'
import './RecipientList.css'

interface StateOption { id: string; name: string; code: string }
interface CarrierOption { id: string; name: string }

interface FilterChip { key: string; label: string; onRemove: () => void }

function buildFilterChips(
  filters: FilterTypes,
  clear: (patch: Partial<FilterTypes>) => void,
  states: StateOption[],
  carriers: CarrierOption[],
): FilterChip[] {
  const chips: FilterChip[] = []

  const fromOptions = (values: string[], opts: {value: string; label: string}[], limit = 2): string => {
    const labels = values.map(v => opts.find(o => o.value === v)?.label || v)
    return labels.length <= limit ? labels.join(', ') : `${labels.slice(0, limit).join(', ')} +${labels.length - limit}`
  }

  const fromNamed = <T extends {id: string; name: string}>(ids: string[], list: T[], limit = 2): string => {
    const names = ids.map(id => list.find(i => i.id === id)?.name || id)
    return names.length <= limit ? names.join(', ') : `${names.slice(0, limit).join(', ')} +${names.length - limit}`
  }

  const boolLabel = (v: boolean | undefined) => v === true ? 'Yes' : 'No'

  const POLICY_STATUS_LABELS: Record<string, string> = {
    ACTIVE: 'Active', EXPIRED: 'Expired', CANCELLED: 'Cancelled', PENDING: 'Pending',
    CANCELED: 'Canceled', RENEWED: 'Renewed',
  }
  const CARRIER_STATUS_LABELS: Record<string, string> = {
    IN_FORCE: 'In Force', TERMINATED: 'Terminated', GRACE_PERIOD: 'Grace Period',
    PENDING_EFFECTUATION: 'Pending Effectuation', PAST_DUE: 'Past Due',
    DELINQUENT: 'Delinquent', FUTURE_EFFECTIVE: 'Future Effective', UNKNOWN: 'Unknown',
  }
  const JURISDICTION_LABELS: Record<string, string> = {
    FFM: 'FFM', SBM: 'SBM', EXPANDED_MEDICAID: 'Expanded Medicaid',
  }
  const COMMISSION_STATUS_LABELS: Record<string, string> = {
    NEVER_STARTED: 'Never Started', CANCELED: 'Canceled',
    SIN_COMISION: 'Sin Comisión', STAND_BY: 'Stand By',
  }

  if (filters.state.length > 0) {
    const stateLabels = filters.state.map(id => states.find(s => s.id === id)?.code || id)
    const display = stateLabels.length <= 3 ? stateLabels.join(', ') : `${stateLabels.slice(0, 3).join(', ')} +${stateLabels.length - 3}`
    chips.push({ key: 'state', label: `State: ${display}`, onRemove: () => clear({ state: [] }) })
  }
  if (filters.carrier.length > 0) {
    chips.push({ key: 'carrier', label: `Carrier: ${fromNamed(filters.carrier, carriers)}`, onRemove: () => clear({ carrier: [] }) })
  }
  if (filters.policyStatus.length > 0) {
    const labels = filters.policyStatus.map(v => POLICY_STATUS_LABELS[v] || v)
    chips.push({ key: 'policyStatus', label: `Policy Status: ${labels.join(', ')}`, onRemove: () => clear({ policyStatus: [] }) })
  }
  if (filters.policyType.length > 0) {
    chips.push({ key: 'policyType', label: `Policy Type: ${filters.policyType.join(', ')}`, onRemove: () => clear({ policyType: [] }) })
  }
  if (filters.isPolicyHolder !== undefined) {
    chips.push({ key: 'isPolicyHolder', label: `Policy Holder: ${boolLabel(filters.isPolicyHolder)}`, onRemove: () => clear({ isPolicyHolder: undefined }) })
  }
  if (filters.withoutPolicy !== undefined) {
    chips.push({ key: 'withoutPolicy', label: `Without Policy: ${boolLabel(filters.withoutPolicy)}`, onRemove: () => clear({ withoutPolicy: undefined }) })
  }
  if (filters.unassigned !== undefined) {
    chips.push({ key: 'unassigned', label: `Unassigned: ${boolLabel(filters.unassigned)}`, onRemove: () => clear({ unassigned: undefined }) })
  }
  if (filters.user.length > 0) {
    chips.push({ key: 'user', label: `Agent (${filters.user.length})`, onRemove: () => clear({ user: [] }) })
  }
  if (filters.assignedTo.length > 0) {
    chips.push({ key: 'assignedTo', label: `Assigned To (${filters.assignedTo.length})`, onRemove: () => clear({ assignedTo: [] }) })
  }
  if (filters.stateJurisdictionType) {
    chips.push({ key: 'stateJurisdictionType', label: `Jurisdiction: ${JURISDICTION_LABELS[filters.stateJurisdictionType] || filters.stateJurisdictionType}`, onRemove: () => clear({ stateJurisdictionType: '' }) })
  }
  if (filters.zipCode) {
    chips.push({ key: 'zipCode', label: `Zip: ${filters.zipCode}`, onRemove: () => clear({ zipCode: '' }) })
  }
  if (filters.immigrationStatus.length > 0) {
    chips.push({ key: 'immigrationStatus', label: `Immigration: ${fromOptions(filters.immigrationStatus, IMMIGRATION_STATUS_CHOICES)}`, onRemove: () => clear({ immigrationStatus: [] }) })
  }
  if (filters.documentStatus.length > 0) {
    chips.push({ key: 'documentStatus', label: `Doc Status: ${fromOptions(filters.documentStatus, DOCUMENT_STATUS_OPTIONS)}`, onRemove: () => clear({ documentStatus: [] }) })
  }
  if (filters.paymentStatus.length > 0) {
    chips.push({ key: 'paymentStatus', label: `Payment: ${fromOptions(filters.paymentStatus, PAYMENT_STATUS_OPTIONS)}`, onRemove: () => clear({ paymentStatus: [] }) })
  }
  if (filters.leadSource.length > 0) {
    chips.push({ key: 'leadSource', label: `Source (${filters.leadSource.length})`, onRemove: () => clear({ leadSource: [] }) })
  }
  if (filters.metalLevel) {
    const label = METAL_LEVELS.find(o => o.value === filters.metalLevel)?.label || filters.metalLevel
    chips.push({ key: 'metalLevel', label: `Metal: ${label}`, onRemove: () => clear({ metalLevel: '' }) })
  }
  if (filters.holderGender) {
    chips.push({ key: 'holderGender', label: `Gender: ${filters.holderGender}`, onRemove: () => clear({ holderGender: '' }) })
  }
  if (filters.hasMemberId !== undefined) {
    chips.push({ key: 'hasMemberId', label: `Member ID: ${boolLabel(filters.hasMemberId)}`, onRemove: () => clear({ hasMemberId: undefined }) })
  }
  if (filters.commissionPayable !== undefined) {
    chips.push({ key: 'commissionPayable', label: `Commission Payable: ${boolLabel(filters.commissionPayable)}`, onRemove: () => clear({ commissionPayable: undefined }) })
  }
  if (filters.commissionPaymentStatus) {
    chips.push({ key: 'commissionPaymentStatus', label: `Commission Status: ${COMMISSION_STATUS_LABELS[filters.commissionPaymentStatus] || filters.commissionPaymentStatus}`, onRemove: () => clear({ commissionPaymentStatus: '' }) })
  }
  if (filters.ownsCar !== undefined) {
    chips.push({ key: 'ownsCar', label: `Owns Car: ${boolLabel(filters.ownsCar)}`, onRemove: () => clear({ ownsCar: undefined }) })
  }
  if (filters.ownsHome !== undefined) {
    chips.push({ key: 'ownsHome', label: `Owns Home: ${boolLabel(filters.ownsHome)}`, onRemove: () => clear({ ownsHome: undefined }) })
  }
  if (filters.hasAutoPolicy !== undefined) {
    chips.push({ key: 'hasAutoPolicy', label: `Has Auto Policy: ${boolLabel(filters.hasAutoPolicy)}`, onRemove: () => clear({ hasAutoPolicy: undefined }) })
  }
  if (filters.hasHomePolicy !== undefined) {
    chips.push({ key: 'hasHomePolicy', label: `Has Home Policy: ${boolLabel(filters.hasHomePolicy)}`, onRemove: () => clear({ hasHomePolicy: undefined }) })
  }
  if (filters.policyCreatedFrom || filters.policyCreatedTo) {
    const range = [filters.policyCreatedFrom, filters.policyCreatedTo].filter(Boolean).join(' → ')
    chips.push({ key: 'policyCreated', label: `Created: ${range}`, onRemove: () => clear({ policyCreatedFrom: '', policyCreatedTo: '' }) })
  }
  if (filters.submissionDateFrom || filters.submissionDateTo) {
    const range = [filters.submissionDateFrom, filters.submissionDateTo].filter(Boolean).join(' → ')
    chips.push({ key: 'submissionDate', label: `Submitted: ${range}`, onRemove: () => clear({ submissionDateFrom: '', submissionDateTo: '' }) })
  }
  if (filters.carrierStatusGeneric.length > 0) {
    const labels = filters.carrierStatusGeneric.map(v => CARRIER_STATUS_LABELS[v] || v)
    chips.push({ key: 'carrierStatus', label: `Carrier Status: ${labels.join(', ')}`, onRemove: () => clear({ carrierStatusGeneric: [] }) })
  }
  if (filters.carrierLastAsOfDateAfter || filters.carrierLastAsOfDateBefore) {
    const range = [filters.carrierLastAsOfDateAfter, filters.carrierLastAsOfDateBefore].filter(Boolean).join(' → ')
    chips.push({ key: 'carrierAsOf', label: `As-of: ${range}`, onRemove: () => clear({ carrierLastAsOfDateAfter: '', carrierLastAsOfDateBefore: '' }) })
  }
  if (filters.carrierIsAgeIn !== undefined) {
    chips.push({ key: 'carrierIsAgeIn', label: `Age-In: ${boolLabel(filters.carrierIsAgeIn)}`, onRemove: () => clear({ carrierIsAgeIn: undefined }) })
  }
  if (filters.carrierIsDelinquent !== undefined) {
    chips.push({ key: 'carrierIsDelinquent', label: `Delinquent: ${boolLabel(filters.carrierIsDelinquent)}`, onRemove: () => clear({ carrierIsDelinquent: undefined }) })
  }
  if (filters.carrierIsUnpaidPending !== undefined) {
    chips.push({ key: 'carrierIsUnpaidPending', label: `Unpaid Pending: ${boolLabel(filters.carrierIsUnpaidPending)}`, onRemove: () => clear({ carrierIsUnpaidPending: undefined }) })
  }
  if (filters.carrierIsUnpaidPastDue !== undefined) {
    chips.push({ key: 'carrierIsUnpaidPastDue', label: `Unpaid Past Due: ${boolLabel(filters.carrierIsUnpaidPastDue)}`, onRemove: () => clear({ carrierIsUnpaidPastDue: undefined }) })
  }

  return chips
}

interface RecipientListProps {
  filters: FilterTypes
  setFilters: React.Dispatch<React.SetStateAction<FilterTypes>>
  selectedContactIds: string[]
  onSelectionChange: (ids: string[]) => void
  segmentId?: string | null
  onStatsChange?: (stats: RecipientStats) => void
  onOpenFilters: () => void
  activeFilterCount: number
  states?: StateOption[]
  carriers?: CarrierOption[]
}

export const RecipientList: React.FC<RecipientListProps> = ({
  filters,
  setFilters,
  selectedContactIds,
  onSelectionChange,
  segmentId,
  onStatsChange,
  onOpenFilters,
  activeFilterCount,
  states = [],
  carriers = [],
}) => {
  const [searchTerm, setSearchTerm] = useState(filters.search)
  const [first, setFirst] = useState(20)
  const [currentPage, setCurrentPage] = useState(1)
  const [debouncedSearch, setDebouncedSearch] = useState(filters.search)
  const [showBulkTagModal, setShowBulkTagModal] = useState(false)
  const [confirmTargetId, setConfirmTargetId] = useState<string | null>(null)
  const [previewContactId, setPreviewContactId] = useState<string | null>(null)
  const [tagTargetId, setTagTargetId] = useState<string | null>(null)

  const [getContacts, {data, loading}] = useLazyQuery<IResRecipientsAPI>(GET_ALL_RECIPIENTS, {
    fetchPolicy: 'network-only',
  })

  const [removeFromRecipients, {loading: removing}] = useMutation(REMOVE_FROM_RECIPIENTS)
  const [bulkUnsubscribe, {loading: unsubscribing}] = useMutation(BULK_UNSUBSCRIBE_CONTACTS)

  const clearFilter = (patch: Partial<FilterTypes>) => {
    setFilters(prev => ({...prev, ...patch}))
  }

  const filterChips = buildFilterChips(filters, clearFilter, states, carriers)

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm)
    }, 300)
    return () => clearTimeout(timer)
  }, [searchTerm])

  // Reset to page 1 when advanced filters change
  useEffect(() => {
    setCurrentPage(1)
  }, [
    filters.status, segmentId, filters.tags, filters.effectiveYears,
    filters.state, filters.carrier, filters.policyStatus, filters.policyType,
    filters.isPolicyHolder, filters.withoutPolicy, filters.unassigned,
    filters.user, filters.assignedTo, filters.stateJurisdictionType, filters.zipCode,
    filters.immigrationStatus, filters.documentStatus, filters.paymentStatus, filters.leadSource,
    filters.metalLevel, filters.holderGender, filters.hasMemberId,
    filters.commissionPayable, filters.commissionPaymentStatus,
    filters.ownsCar, filters.ownsHome, filters.hasAutoPolicy, filters.hasHomePolicy,
    filters.policyCreatedFrom, filters.policyCreatedTo, filters.submissionDateFrom, filters.submissionDateTo,
    filters.carrierStatusGeneric, filters.carrierLastAsOfDateAfter, filters.carrierLastAsOfDateBefore,
    filters.carrierIsAgeIn, filters.carrierIsDelinquent, filters.carrierIsUnpaidPending, filters.carrierIsUnpaidPastDue,
  ])

  const buildQueryVars = (overrides?: Record<string, any>) => ({
    name: debouncedSearch || undefined,
    first,
    offset: (currentPage - 1) * first,
    status: filters.status !== 'ALL' ? filters.status : undefined,
    segment: segmentId || undefined,
    tags: filters.tags && filters.tags.length > 0 ? filters.tags.join(',') : undefined,
    effectiveYear: filters.effectiveYears && filters.effectiveYears.length > 0 ? filters.effectiveYears.join(',') : undefined,
    state: filters.state && filters.state.length > 0 ? filters.state.join(',') : undefined,
    carrier: filters.carrier && filters.carrier.length > 0 ? filters.carrier.join(',') : undefined,
    policyStatus: filters.policyStatus && filters.policyStatus.length > 0 ? filters.policyStatus.join(',') : undefined,
    policyType: filters.policyType && filters.policyType.length > 0 ? filters.policyType.join(',') : undefined,
    isPolicyHolder: filters.isPolicyHolder,
    withoutPolicy: filters.withoutPolicy,
    unassigned: filters.unassigned,
    user: filters.user && filters.user.length > 0 ? filters.user.join(',') : undefined,
    assignedTo: filters.assignedTo && filters.assignedTo.length > 0 ? filters.assignedTo.join(',') : undefined,
    stateJurisdictionType: filters.stateJurisdictionType || undefined,
    zipCode: filters.zipCode || undefined,
    immigrationStatus: filters.immigrationStatus && filters.immigrationStatus.length > 0 ? filters.immigrationStatus.join(',') : undefined,
    documentStatus: filters.documentStatus && filters.documentStatus.length > 0 ? filters.documentStatus.join(',') : undefined,
    paymentStatus: filters.paymentStatus && filters.paymentStatus.length > 0 ? filters.paymentStatus.join(',') : undefined,
    leadSource: filters.leadSource && filters.leadSource.length > 0 ? filters.leadSource.join(',') : undefined,
    metalLevel: filters.metalLevel || undefined,
    gender: filters.holderGender || undefined,
    hasMemberId: filters.hasMemberId,
    commissionPayable: filters.commissionPayable,
    commissionPaymentStatus: filters.commissionPaymentStatus || undefined,
    ownsCar: filters.ownsCar,
    ownsHome: filters.ownsHome,
    hasAutoPolicy: filters.hasAutoPolicy,
    hasHomePolicy: filters.hasHomePolicy,
    policyCreatedFrom: filters.policyCreatedFrom || undefined,
    policyCreatedTo: filters.policyCreatedTo || undefined,
    submissionDateFrom: filters.submissionDateFrom || undefined,
    submissionDateTo: filters.submissionDateTo || undefined,
    carrierStatusGeneric: filters.carrierStatusGeneric && filters.carrierStatusGeneric.length > 0 ? filters.carrierStatusGeneric.join(',') : undefined,
    carrierLastAsOfDateAfter: filters.carrierLastAsOfDateAfter || undefined,
    carrierLastAsOfDateBefore: filters.carrierLastAsOfDateBefore || undefined,
    carrierIsAgeIn: filters.carrierIsAgeIn,
    carrierIsDelinquent: filters.carrierIsDelinquent,
    carrierIsUnpaidPending: filters.carrierIsUnpaidPending,
    carrierIsUnpaidPastDue: filters.carrierIsUnpaidPastDue,
    ...overrides,
  })

  // Fetch contacts when filters or search change
  useEffect(() => {
    getContacts({ variables: buildQueryVars() })
  }, [debouncedSearch, currentPage, first, filters.status, segmentId, filters.tags, filters.effectiveYears,
    filters.state, filters.carrier, filters.policyStatus, filters.policyType,
    filters.isPolicyHolder, filters.withoutPolicy, filters.unassigned,
    filters.user, filters.assignedTo, filters.stateJurisdictionType, filters.zipCode,
    filters.immigrationStatus, filters.documentStatus, filters.paymentStatus, filters.leadSource,
    filters.metalLevel, filters.holderGender, filters.hasMemberId,
    filters.commissionPayable, filters.commissionPaymentStatus,
    filters.ownsCar, filters.ownsHome, filters.hasAutoPolicy, filters.hasHomePolicy,
    filters.policyCreatedFrom, filters.policyCreatedTo, filters.submissionDateFrom, filters.submissionDateTo,
    filters.carrierStatusGeneric, filters.carrierLastAsOfDateAfter, filters.carrierLastAsOfDateBefore,
    filters.carrierIsAgeIn, filters.carrierIsDelinquent, filters.carrierIsUnpaidPending, filters.carrierIsUnpaidPastDue,
    getContacts]) // eslint-disable-line react-hooks/exhaustive-deps

  const contacts = data?.allRecipients?.edges || []
  const totalCount = data?.allRecipients?.totalCount || 0
  const stats = data?.recipientStats

  // Pass stats to parent
  useEffect(() => {
    if (stats && onStatsChange) {
      onStatsChange({
        total: stats.totalRecipients,
        subscribed: stats.subscribedCount,
        unsubscribed: stats.unsubscribedCount,
      })
    }
  }, [stats, onStatsChange])
  const totalPages = Math.ceil(totalCount / first)
  const {start, end} = PaginationUtils.getPaginationRange(currentPage, first, totalCount)

  const isAllSelected = contacts.length > 0 && contacts.every(edge => selectedContactIds.includes(edge.node.id))

  const toggleSelectAll = () => {
    if (isAllSelected) {
      const currentIds = contacts.map(edge => edge.node.id)
      onSelectionChange(selectedContactIds.filter(id => !currentIds.includes(id)))
    } else {
      const currentIds = contacts.map(edge => edge.node.id)
      const combined = [...selectedContactIds, ...currentIds]
      const unique = combined.filter((id, index) => combined.indexOf(id) === index)
      onSelectionChange(unique)
    }
  }

  const toggleSelect = (contactId: string) => {
    if (selectedContactIds.includes(contactId)) {
      onSelectionChange(selectedContactIds.filter(id => id !== contactId))
    } else {
      onSelectionChange([...selectedContactIds, contactId])
    }
  }

  const handleBulkDelete = async () => {
    if (!window.confirm(`Are you sure you want to delete ${selectedContactIds.length} contact(s)?`)) {
      return
    }
    // TODO: Implement delete mutation
    alert('Bulk delete not implemented yet')
  }

  const handleRefresh = () => {
    getContacts({ variables: buildQueryVars() })
  }

  const handleSearch = () => {
    setCurrentPage(1)
    setDebouncedSearch(searchTerm)
  }

  const handleRemoveFromRecipients = async () => {
    if (!window.confirm(`Remove ${selectedContactIds.length} contact(s) from recipients list?`)) {
      return
    }

    try {
      const result = await removeFromRecipients({
        variables: {contactIds: selectedContactIds},
      })
      toast.success(`${result.data.removeFromRecipients.removedCount} contact(s) removed`)
      onSelectionChange([])
      handleRefresh()
    } catch (error: any) {
      toast.error(error.message || 'Failed to remove from recipients')
    }
  }

  const handleBulkUnsubscribe = async () => {
    if (!window.confirm(`Unsubscribe ${selectedContactIds.length} contact(s) from all campaigns?`)) {
      return
    }

    try {
      const result = await bulkUnsubscribe({
        variables: {
          contactIds: selectedContactIds,
          reason: 'Bulk unsubscribe from admin panel',
        },
      })
      toast.success(`${result.data.bulkUnsubscribeContacts.unsubscribedCount} contact(s) unsubscribed`)
      onSelectionChange([])
      handleRefresh()
    } catch (error: any) {
      toast.error(error.message || 'Failed to unsubscribe contacts')
    }
  }

  const confirmUnsubscribe = (contactId: string) => {
    setConfirmTargetId(contactId)
  }

  const executeUnsubscribe = async () => {
    if (!confirmTargetId) return
    try {
      await bulkUnsubscribe({
        variables: {
          contactIds: [confirmTargetId],
          reason: 'Unsubscribed from recipients list',
        },
      })
      toast.success('Contact unsubscribed')
      onSelectionChange(selectedContactIds.filter((id) => id !== confirmTargetId))
      setConfirmTargetId(null)
      handleRefresh()
    } catch (error: any) {
      toast.error(error.message || 'Failed to unsubscribe contact')
    }
  }



  return (
    <div className='recipient-list'>
      {/* Header with Search */}
      <div className='list-header mb-4'>
        <InputGroup className='recipients-search-toolbar'>
          <InputGroup.Text>
            <i className='bi bi-search'></i>
          </InputGroup.Text>
          <Form.Control
            type='text'
            placeholder='Search by name, email, or phone...'
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault()
                handleSearch()
              }
            }}
          />
          <Button
            variant='outline-success'
            style={{border: '1px solid #dbdfe9'}}
            onClick={handleSearch}
          >
            Search
          </Button>
          <Button
            variant='outline-primary'
            style={{border: '1px solid #dbdfe9'}}
            onClick={onOpenFilters}
          >
            Filter
            {activeFilterCount > 0 && (
              <Badge bg='light' text='primary' pill className='ms-2'>{activeFilterCount}</Badge>
            )}
          </Button>
        </InputGroup>
      </div>

      {/* Active filter chips */}
      {filterChips.length > 0 && (
        <div className='filter-chips-bar mb-3'>
          {filterChips.map(chip => (
            <span key={chip.key} className='filter-chip'>
              {chip.label}
              <button
                type='button'
                className='filter-chip-remove'
                onClick={chip.onRemove}
                aria-label={`Remove ${chip.label} filter`}
              >
                ×
              </button>
            </span>
          ))}
          <button
            type='button'
            className='btn btn-link btn-sm p-0 ms-1 text-danger'
            onClick={() => setFilters(prev => ({
              ...prev,
              state: [], carrier: [], policyStatus: [], policyType: [],
              isPolicyHolder: undefined, withoutPolicy: undefined, unassigned: undefined,
              user: [], assignedTo: [], stateJurisdictionType: '', zipCode: '',
              immigrationStatus: [], documentStatus: [], paymentStatus: [], leadSource: [],
              metalLevel: '', holderGender: '', hasMemberId: undefined,
              commissionPayable: undefined, commissionPaymentStatus: '',
              ownsCar: undefined, ownsHome: undefined, hasAutoPolicy: undefined, hasHomePolicy: undefined,
              policyCreatedFrom: '', policyCreatedTo: '', submissionDateFrom: '', submissionDateTo: '',
              carrierStatusGeneric: [], carrierLastAsOfDateAfter: '', carrierLastAsOfDateBefore: '',
              carrierIsAgeIn: undefined, carrierIsDelinquent: undefined,
              carrierIsUnpaidPending: undefined, carrierIsUnpaidPastDue: undefined,
            }))}
          >
            Clear all
          </button>
        </div>
      )}

      {/* Bulk Actions */}
      {selectedContactIds.length > 0 && (
        <div className='bulk-actions-bar mb-3'>
          <div className='d-flex justify-content-between align-items-center'>
            <span className='text-muted'>
              <strong>{selectedContactIds.length}</strong> contact{selectedContactIds.length !== 1 && 's'} selected
            </span>
            <div className='d-flex gap-2'>
              <Button variant='outline-primary' size='sm' onClick={() => setShowBulkTagModal(true)}>
                <i className='bi bi-tags me-1'></i>
                Manage Tags
              </Button>
              <Button variant='outline-danger' size='sm' onClick={handleBulkDelete}>
                <i className='bi bi-trash me-1'></i>
                Delete
              </Button>
              <Button variant='outline-warning' size='sm' onClick={handleRemoveFromRecipients} disabled={removing}>
                <i className='bi bi-person-dash me-1'></i>
                {removing ? 'Removing...' : 'Remove from Recipients'}
              </Button>
              <Button variant='outline-danger' size='sm' onClick={handleBulkUnsubscribe} disabled={unsubscribing}>
                <i className='bi bi-envelope-slash me-1'></i>
                {unsubscribing ? 'Unsubscribing...' : 'Bulk Unsubscribe'}
              </Button>
              <Button variant='outline-secondary' size='sm' onClick={() => onSelectionChange([])}>
                <i className='bi bi-x me-1'></i>
                Clear Selection
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Table */}
      <div className='results-header'>
        <PaginationSection
          first={first}
          setFirst={(value) => {
            setFirst(value)
            setCurrentPage(1)
          }}
          currentPage={currentPage}
          totalPages={totalPages}
          start={start}
          end={end}
          totalPolicies={totalCount}
          updatePage={setCurrentPage}
          PaginationUtils={PaginationUtils}
        />
      </div>

      <div className='table-container'>
        {loading && contacts.length === 0 ? (
          <div className='text-center py-5'>
            <Spinner animation='border' />
          </div>
        ) : contacts.length === 0 ? (
          <div className='text-center py-5 text-muted'>
            <i className='bi bi-inbox fs-1 d-block mb-2'></i>
            <p>No recipients found</p>
          </div>
        ) : (
          <Table hover responsive>
            <thead>
              <tr>
                <th style={{width: '50px'}}>
                  <Form.Check
                    type='checkbox'
                    checked={isAllSelected}
                    onChange={toggleSelectAll}
                  />
                </th>
                <th>Name</th>
                <th>Email</th>
                <th>Phone</th>
                <th>Tags</th>
                <th>Status</th>
                <th style={{width: '100px'}}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {contacts.map(({node: contact}) => (
                <tr key={contact.id}>
                  <td>
                    <Form.Check
                      type='checkbox'
                      checked={selectedContactIds.includes(contact.id)}
                      onChange={() => toggleSelect(contact.id)}
                    />
                  </td>
                  <td>
                    <strong>{contact.fullName || `${contact.firstName} ${contact.lastName}`}</strong>
                  </td>
                  <td>{contact.email || <span className='text-muted'>—</span>}</td>
                  <td>
                    <PhoneProviderActions phone={contact.phone} emptyText='—' />
                  </td>
                  <td>
                    {contact.tagAssignments && contact.tagAssignments.length > 0 ? (
                      <div className='d-flex flex-wrap gap-1'>
                        {contact.tagAssignments.map((assignment) => (
                          <span
                            key={assignment.tag.id}
                            className='badge rounded-pill'
                            style={{
                              backgroundColor: assignment.tag.color,
                              color: getTagContrastColor(assignment.tag.color),
                              fontSize: '0.7rem',
                            }}
                          >
                            {assignment.tag.name}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <span className='text-muted small'>No tags</span>
                    )}
                  </td>
                  <td>
                    {contact.subscriptionStatus === 'SUBSCRIBED' ? (
                      <Badge bg='success' className='subscription-badge'>
                        <i className='bi bi-check-circle me-1'></i>
                        Subscribed
                      </Badge>
                    ) : (
                      <Badge bg='danger' className='subscription-badge'>
                        <i className='bi bi-x-circle me-1'></i>
                        Unsubscribed
                      </Badge>
                    )}
                  </td>
                  <td>
                    <Dropdown as={ButtonGroup} size='sm'>
                      <Button variant='outline-primary' size='sm'>
                        View
                      </Button>
                      <Dropdown.Toggle split variant='outline-primary' size='sm' />
                      <Dropdown.Menu>
                          <Dropdown.Item onClick={() => setPreviewContactId(contact.id)}>
                          <i className='bi bi-eye me-2'></i>
                          View
                        </Dropdown.Item>
                        <Dropdown.Item onClick={() => setTagTargetId(contact.id)}>
                          <i className='bi bi-tags me-2'></i>
                          Manage Tags
                        </Dropdown.Item>
                        <Dropdown.Divider />
                        <Dropdown.Item
                          className='text-danger'
                          onClick={() => confirmUnsubscribe(contact.id)}
                          disabled={unsubscribing}
                        >
                          <i className='bi bi-x-circle me-2'></i>
                          {unsubscribing ? 'Unsubscribing...' : 'Unsubscribe'}
                        </Dropdown.Item>
                      </Dropdown.Menu>
                    </Dropdown>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </div>

      {/* Bulk Tag Modal */}
      <BulkTagModal
        show={showBulkTagModal}
        onHide={() => setShowBulkTagModal(false)}
        selectedContactIds={selectedContactIds}
        onSuccess={() => {
          handleRefresh()
          onSelectionChange([])
        }}
      />

      {/* Per-row Tag Modal */}
      {tagTargetId && (
        <BulkTagModal
          show={Boolean(tagTargetId)}
          onHide={() => setTagTargetId(null)}
          selectedContactIds={[tagTargetId]}
          onSuccess={() => {
            setTagTargetId(null)
            handleRefresh()
          }}
        />
      )}

      {/* Contact Preview Modal */}
      {previewContactId && (
        <ModalPreview idContact={previewContactId} onClose={() => setPreviewContactId(null)} />
      )}

      <Modal show={Boolean(confirmTargetId)} onHide={() => setConfirmTargetId(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Confirm unsubscribe</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          This contact will be unsubscribed from all campaigns. Do you want to continue?
        </Modal.Body>
        <Modal.Footer>
          <Button variant='secondary' onClick={() => setConfirmTargetId(null)} disabled={unsubscribing}>
            Cancel
          </Button>
          <Button variant='danger' onClick={executeUnsubscribe} disabled={unsubscribing}>
            {unsubscribing ? 'Unsubscribing...' : 'Unsubscribe'}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  )
}
